<!-- TODO these component has more than 350+ LOC try to DRY and improve flexibility -->
<template>
  <BgImageLayout title="Kanban Board">
    <TodoModal
      v-model="createTodoModal.open"
      :state-id="createTodoModal.stateId"
      :states-with-tasks="board ? board.states : []"
      from-page="kanban"
      @new-todo="todoCreated"
    />

    <TodoModal
      v-model="editTodoModal.open"
      :state-id="editTodoModal.stateId"
      :states-with-tasks="board ? board.states : []"
      :edit-item="editTodoModal.task"
      from-page="kanban"
      @updated-todo="todoUpdated"
      @delete="deleteTodo(editTodoModal.task.id)"
    />

    <div class="board">
      <BoardHeader :board="board" />

      <transition name="fade" mode="out-in">
        <Container
          v-if="board"
          key="states"
          drag-handle-selector=".drag-handle"
          class="states"
          orientation="horizontal"
          @drop="onStateDrop($event)"
        >
          <Draggable v-for="(state, index) of filteredStates" :key="state.id" class="state">
            <StateHeader :state="state" :order="index + 1" @new-todo="openCreateTodoModal(state.id)" />

            <Container
              :get-child-payload="getCardPayload(state.id)"
              group-name="state"
              drag-class="card-ghost"
              class="state__body"
              @drop="onDrop(state.id, $event)"
              @drag-start="dragTaskStarted"
              @drag-end="dragTaskEnded"
              @hook:mounted="containerMounted"
            >
              <Draggable v-for="task of state.tasks" :key="task.id" class="single-task">
                <SingleTask ref="singleTask" :task="task" @click.native="openEditModal(task, state.id)" />
              </Draggable>
            </Container>
          </Draggable>
        </Container>
        <div v-else key="loaders" class="state-loaders">
          <StatesLoader :key="1" />
          <StatesLoader :key="2" />
          <StatesLoader :key="3" />
          <StatesLoader :key="4" />
        </div>
      </transition>
    </div>
  </BgImageLayout>
</template>

<script>
import KanbanBoardModule from './vuex/KanbanBoard'
import TodoModalModule from '@/reusable/TodoModal/vuex/TodoModal'
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex'
import { cloneDeep } from 'lodash'
import * as apiTo from './api'
import { numberBetween } from '@/lib/generic.utils'
import axios from 'axios'
// Components
import TodoModal from '@/reusable/TodoModal/TodoModal'
import BoardHeader from './components/BoardHeader'
import SingleTask from './components/SingleTask'
import StatesLoader from './components/StatesLoader'
import StateHeader from './components/StateHeader'
import { Container, Draggable } from 'vue-smooth-dnd'

export default {
  components: {
    BoardHeader,
    SingleTask,
    StatesLoader,
    StateHeader,
    TodoModal,
    Container,
    Draggable
  },

  props: {
    id: String // from route
  },

  data: () => ({
    createTodoModal: {
      open: false,
      stateId: null
    },
    editTodoModal: {
      open: false,
      stateId: null,
      task: null
    },
    oldMouseClientX: null,
    mouseMoving: null
  }),

  computed: {
    ...mapState('kanban-board', ['activeParticipantsIds']),
    ...mapGetters('kanban-board', ['board']),

    filteredStates() {
      if (!this.board) return
      const board = cloneDeep(this.board)

      if (!this.activeParticipantsIds.length) return board.states
      return board.states.map(state => {
        state.tasks = state.tasks.filter(t => t.owners.some(o => this.activeParticipantsIds.includes(o.id)))
        return state
      })
    }
  },

  created() {
    this.$store.registerModule('kanban-board', KanbanBoardModule)
    this.$store.registerModule('todo-modal', TodoModalModule)
    this.fetchBoard(this.id)
  },

  beforeDestroy() {
    this.resetState()
    this.resetCreateData()
    this.$store.unregisterModule('todo-modal')
    this.$store.unregisterModule('kanban-board')
  },

  methods: {
    ...mapMutations('kanban-board', ['setStates', 'updateTask', 'resetState']),
    ...mapActions('kanban-board', ['fetchBoard', 'deleteTodo', 'todoCreated', 'todoUpdated']),
    ...mapMutations('todo-modal', ['resetCreateData']),

    containerMounted() {
      const cards = this.$el.querySelectorAll('.smooth-dnd-draggable-wrapper')
      cards.forEach(el => {
        el.classList.add('animation-once')
        el.addEventListener('animationend', () => {
          el.classList.remove('animation-once')
        })
      })

      this.$refs.singleTask && this.$refs.singleTask.forEach(el => el.addAnimationsOnce())
    },

    getCardPayload(stateId) {
      const board = cloneDeep(this.board)
      return index => {
        return board.states.find(s => s.id === stateId).tasks[index]
      }
    },

    getUpdatedTaskPayload(task, stateId) {
      return {
        ...task,
        entity_id: task.entity ? task.entity.id : null,
        entity: task.entity ? task.entity.type : null,
        priority_id: task.priority ? task.priority.id : null,
        owner_ids: task.owners.map(o => o.id),
        state_id: stateId
      }
    },

    updateDraggedTask(task, addedIndex, removedIndex, tasks, stateId) {
      if (!tasks.length) task.kanban_position = 65535
      // put task in the first position since there are no tasks
      // if we DnD at the first position
      else if (addedIndex === 0) task.kanban_position = numberBetween(0, tasks[0].kanban_position)
      // DnD in the last position inside the state
      else if (addedIndex >= tasks.length - 1 && task.state.id === stateId)
        task.kanban_position = 65535 + tasks[tasks.length - 1].kanban_position
      // DnD in the last position from another state
      else if (addedIndex > tasks.length - 1 && task.state.id !== stateId)
        task.kanban_position = 65535 + tasks[tasks.length - 1].kanban_position
      // DnD in the same state
      else if (addedIndex !== null && removedIndex !== null) {
        const prevIndex = removedIndex > addedIndex ? addedIndex - 1 : addedIndex
        const nextIndex = addedIndex > removedIndex ? addedIndex + 1 : addedIndex
        task.kanban_position = numberBetween(tasks[prevIndex].kanban_position, tasks[nextIndex].kanban_position)
      } else if (addedIndex !== null) {
        // DnD task in a new state
        const prevPos = tasks[addedIndex - 1].kanban_position
        const nextPos = tasks[addedIndex].kanban_position
        task.kanban_position = numberBetween(prevPos, nextPos)
      } else {
        throw new Error('[Roam]:: KanbanBoard.vue, onDrop(): Un-covered case')
      }
    },

    onDrop(stateId, { addedIndex, removedIndex, payload }) {
      if (addedIndex === removedIndex) return // order didn't changed
      if (removedIndex !== null && addedIndex === null) return // unnecessary call from DnD library
      const task = cloneDeep(payload)
      const state = this.board.states.find(s => s.id === stateId)
      const tasks = state.tasks

      if (addedIndex !== null && removedIndex !== null && [1, -1].includes(addedIndex - removedIndex)) {
        // task in the same state swapped
        this.swapTodos(addedIndex, removedIndex, tasks)
        return
      }

      this.updateDraggedTask(task, addedIndex, removedIndex, tasks, stateId)
      task.state = state
      this.updateTask(task)
      apiTo.updateTask(task.id, this.getUpdatedTaskPayload(task, stateId)).catch(() => {
        this.updateTask(payload)
      })
    },

    swapTodos(addedIndex, removedIndex, todos) {
      const oldTodos = cloneDeep(todos)
      todos[addedIndex].kanban_position = oldTodos[removedIndex].kanban_position
      todos[removedIndex].kanban_position = oldTodos[addedIndex].kanban_position
      this.updateTask(todos[addedIndex])
      this.updateTask(todos[removedIndex])
      axios
        .all([
          apiTo.updateTask(
            todos[addedIndex].id,
            this.getUpdatedTaskPayload(todos[addedIndex], todos[addedIndex].state.id)
          ),
          apiTo.updateTask(
            todos[removedIndex].id,
            this.getUpdatedTaskPayload(todos[removedIndex], todos[removedIndex].state.id)
          )
        ])
        .catch(() => {
          this.updateTask(oldTodos[addedIndex])
          this.updateTask(oldTodos[removedIndex])
        })
    },

    openCreateTodoModal(stateId) {
      this.createTodoModal = {
        open: true,
        stateId
      }
    },

    getStatePosition(addIndex, removeIndex, states) {
      let pos = 65535
      const prevIndex = removeIndex > addIndex ? addIndex - 1 : addIndex
      const nextIndex = addIndex > removeIndex ? addIndex + 1 : addIndex

      if (addIndex === 0) pos = numberBetween(0, states[0].kanban_position)
      else if (addIndex >= states.length - 1) pos += states[states.length - 1].kanban_position
      else {
        const prevPos = states[prevIndex].kanban_position
        const nextPos = states[nextIndex].kanban_position
        pos = numberBetween(prevPos, nextPos)
      }

      return pos
    },

    onStateDrop({ addedIndex, removedIndex }) {
      if (addedIndex === removedIndex) return // order didn't changed
      if ([1, -1].includes(addedIndex - removedIndex)) {
        this.swapStates(addedIndex, removedIndex)
        return
      }

      const states = cloneDeep(this.board.states)
      const oldStates = cloneDeep(this.board.states)
      const state = states[removedIndex]
      const pos = this.getStatePosition(addedIndex, removedIndex, states)

      states.splice(removedIndex, 1, { ...state, kanban_position: pos })
      this.setStates(states)
      apiTo.updateState(state.id, { ...state, kanban_position: pos }).catch(() => {
        this.setStates(oldStates)
      })
    },

    swapStates(addedIndex, removedIndex) {
      const states = cloneDeep(this.board.states)
      const oldStates = cloneDeep(this.board.states)

      states[addedIndex].kanban_position = this.board.states[removedIndex].kanban_position
      states[removedIndex].kanban_position = this.board.states[addedIndex].kanban_position

      this.setStates(states)
      axios
        .all([
          apiTo.updateState(states[addedIndex].id, states[addedIndex]),
          apiTo.updateState(states[removedIndex].id, states[removedIndex])
        ])
        .catch(() => {
          this.setStates(oldStates)
        })
    },

    openEditModal(task, stateId) {
      this.editTodoModal = {
        open: true,
        stateId,
        task
      }
    },

    dragTaskStarted({ isSource }) {
      if (!isSource) return
      document.addEventListener('mousemove', this.taskIsDragged)
    },

    dragTaskEnded({ isSource }) {
      if (!isSource) return
      document.removeEventListener('mousemove', this.taskIsDragged)
      this.oldMouseClientX = null
      this.mouseMoving = null
    },

    taskIsDragged(evt) {
      if (!this.oldMouseClientX) this.oldMouseClientX = evt.clientX
      else {
        if (evt.clientX === this.oldMouseClientX) return
        const draggedEl = document.querySelector('.smooth-dnd-ghost.single-task')
        if (evt.clientX > this.oldMouseClientX) {
          this.mouseMoving = 'right'
          draggedEl.classList.remove('left')
          draggedEl.classList.add('right')
        } else {
          this.mouseMoving = 'left'
          draggedEl.classList.remove('right')
          draggedEl.classList.add('left')
        }
        this.oldMouseClientX = evt.clientX
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/custom-variables'
@import '~@/styles/stylus/mixins'

>>> .q-btn-dropdown .q-btn-dropdown__arrow {
  display none
}

.smooth-dnd-ghost.state {
  transform rotate(-2deg)
  box-shadow 0 0 8px 0 rgba(0, 0, 0, .2)
}

.smooth-dnd-ghost.single-task.left {
  transform rotate(-5deg)
  box-shadow 0 0 8px 0 rgba(0, 0, 0, .2)
}

.smooth-dnd-ghost.single-task.right {
  transform rotate(5deg)
  box-shadow 0 0 8px 0 rgba(0, 0, 0, .2)
}

.board {
  height: calc(100vh - 100px) !important
  box-shadow 0 1px 5px rgba(0,0,0,0.2), 0 2px 2px rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12)
  border-radius 4px
  background #fff
  display grid
  grid-template-rows 60px 1fr
  padding 25px

  styledScrollFor('.lists')

  .states {
    display grid
    grid-template-columns repeat(4, 400px)
    grid-auto-flow column
    justify-content space-between
    gap 10px
    overflow auto hidden
    padding-bottom: 20px;

    &.more-than-4 {
      padding-bottom 20px
    }

    .state {
      display grid
      grid-template-rows auto 1fr
      gap 10px
      align-items start
      background #e8ecf7
      padding 15px
      overflow hidden
      border-radius 5px
      width 400px

      styledScrollFor('&__body')

      &__body {
        display grid
        align-content start
        gap 10px
        height 100%
        overflow auto

        .smooth-dnd-draggable-wrapper {
          overflow unset
          &.animation-once {
            animation fadeInScaleIn 350ms ease-in-out both;
            for num in (1..5) {
              &:nth-child({num}) {
                animation-delay: 50ms * num;
              }
            }
          }

        }
      }
    }
  }

  .state-loaders {
    display grid
    grid-template-columns repeat(4, 400px)
    grid-auto-flow column
    justify-content space-between
    gap 10px
    overflow auto hidden
    padding-bottom: 20px;
  }

}


@keyframes fadeInScaleIn {
  from {
    opacity 0;
    transform scale(0.6) translateY(-8px)
  }

  to {
    opacity 1;
  }
}

.fade-leave-active {
  transition: opacity 300ms ease-in-out;
}
.fade-leave-to {
  opacity: 0.1;
}
</style>
